import { useMemo } from "react";
import { Favorite, LeagueMatchSmallElement, MatchSmallElement } from "../config/types";
import { LanguageLocaleEnum } from "../client/api";
import {
  dayToNumber,
  formatRelativeOrFullDate,
  sortMatchesByTimeFast,
} from "../config/utils";
import { translate, showTranslated } from "../config/translator";
import { ValueStore } from "../store/constants";
import { Section, SectionItem } from "../views/components/LeagueMatches";

const NO_LEAGUE_ID = -1;
const FAVORITES_SECTION_ID = "favorites";

type MatchesSection = Section & {
  meta?: {
    isFavorites?: boolean;
  };
};

type UseMatchesSectionsParams = {
  leagueMatchesResponse?: ReadonlyArray<LeagueMatchSmallElement>;
  matchElements?: ValueStore<MatchSmallElement[]> | undefined;
  favorites: Favorite[];
  selectedDate: Date;
  realLeagueId: number;
  language: LanguageLocaleEnum;
};

const EMPTY_MATCHES: ReadonlyArray<MatchSmallElement> = Object.freeze([]);
const EMPTY_GROUPS: ReadonlyArray<LeagueMatchSmallElement> = Object.freeze([]);

const getFavoriteMatches = (
  leagueMatches: ReadonlyArray<LeagueMatchSmallElement>,
  favorites: Favorite[],
  selectedDate: Date
): Array<MatchSmallElement & { idx: number; idx2: number }> => {
  const followedMatchIds = new Set(
    favorites.filter((favorite) => favorite.type === "match").map((favorite) => favorite.id)
  );
  const followedTeamIds = new Set(
    favorites.filter((favorite) => favorite.type === "team").map((favorite) => favorite.id)
  );

  const matchesWithIndex: Array<MatchSmallElement & { idx: number; idx2: number }> = [];

  leagueMatches.forEach((leagueMatch, leagueMatchIndex) => {
    if (!leagueMatch || !Array.isArray(leagueMatch.matches)) {
      return;
    }

    leagueMatch.matches.forEach((match, matchIndex) => {
      if (dayToNumber(new Date(match.startTime)) !== dayToNumber(selectedDate)) {
        return;
      }

      const isFollowedMatch = followedMatchIds.has(match.id);
      const isFollowedTeamMatch =
        followedTeamIds.has(match.team1.id) || followedTeamIds.has(match.team2.id);

      if (isFollowedMatch || isFollowedTeamMatch) {
        matchesWithIndex.push({ ...match, idx: leagueMatchIndex, idx2: matchIndex });
      }
    });
  });

  return matchesWithIndex.sort((firstMatch, secondMatch) =>
    sortMatchesByTimeFast(firstMatch, secondMatch)
  );
};

const buildHomeSections = (
  leagueMatches: ReadonlyArray<LeagueMatchSmallElement>,
  favorites: Favorite[],
  selectedDate: Date,
  language: LanguageLocaleEnum
): MatchesSection[] => {
  const sections = leagueMatches
    .filter(
      (leagueMatch): leagueMatch is LeagueMatchSmallElement =>
        Boolean(leagueMatch) && Array.isArray(leagueMatch.matches)
    )
    .map((leagueMatch, leagueMatchIndex): MatchesSection => {
      const items: SectionItem[] = leagueMatch.matches
        .filter(
          (match) =>
            dayToNumber(new Date(match.startTime)) === dayToNumber(selectedDate)
        )
        .map((match, matchIndex) => ({ ...match, idx: leagueMatchIndex, idx2: matchIndex }))
        .sort((firstMatch, secondMatch) => sortMatchesByTimeFast(firstMatch, secondMatch));

      return {
        title: {
          name: showTranslated(leagueMatch.leagueTournamentName, language),
          id: leagueMatch.leagueTournamentId,
        },
        data: items,
      };
    })
    .filter((section) => section.data.length > 0);

  const favoriteMatches = getFavoriteMatches(leagueMatches, favorites, selectedDate);
  if (favoriteMatches.length === 0) {
    return sections;
  }

  const favoritesSection: MatchesSection = {
    title: {
      name: translate("favorites", language),
      id: FAVORITES_SECTION_ID as unknown as number,
    },
    data: favoriteMatches,
    meta: { isFavorites: true },
  };

  return [favoritesSection, ...sections];
};

const buildLeagueSections = (
  matches: ReadonlyArray<MatchSmallElement>,
  realLeagueId: number,
  language: LanguageLocaleEnum
): MatchesSection[] =>
  matches
    .map((match, matchIndex) => ({ ...match, idx: matchIndex, idx2: -1 }))
    .sort((firstMatch, secondMatch) => sortMatchesByTimeFast(firstMatch, secondMatch))
    .reduce(
      (
        groupedMatches: Array<{
          date: Date;
          matches: Array<MatchSmallElement & { idx: number; idx2: number }>;
        }>,
        matchWithIndex
      ) => {
        const currentDate = new Date(matchWithIndex.startTime);
        const existingDateIndex = groupedMatches.findIndex(
          (matchGroup) => dayToNumber(matchGroup.date) === dayToNumber(currentDate)
        );
        if (existingDateIndex === -1) {
          return groupedMatches.concat([{ date: currentDate, matches: [matchWithIndex] }]);
        }
        const updatedGroups = [...groupedMatches];
        updatedGroups[existingDateIndex] = {
          ...updatedGroups[existingDateIndex],
          matches: [...updatedGroups[existingDateIndex].matches, matchWithIndex],
        };
        return updatedGroups;
      },
      []
    )
    .map<MatchesSection>((dateGroup) => ({
      title: {
        name: formatRelativeOrFullDate(new Date(dateGroup.date), language),
        id: realLeagueId,
      },
      data: dateGroup.matches,
    }));

export const useMatchesSections = ({
  leagueMatchesResponse,
  matchElements,
  favorites,
  selectedDate,
  realLeagueId,
  language,
}: UseMatchesSectionsParams): {
  sections: MatchesSection[];
  leagueMatches: ReadonlyArray<LeagueMatchSmallElement>;
  matches: ReadonlyArray<MatchSmallElement>;
} => {
  const leagueMatches = useMemo(
    () => leagueMatchesResponse ?? EMPTY_GROUPS,
    [leagueMatchesResponse]
  );

  const matches = useMemo(() => {
    if (matchElements && matchElements.id === realLeagueId) {
      return matchElements.response ?? EMPTY_MATCHES;
    }
    return EMPTY_MATCHES;
  }, [matchElements, realLeagueId]);

  const sections = useMemo<MatchesSection[]>(() => {
    if (!selectedDate) {
      return [];
    }

    if (realLeagueId === NO_LEAGUE_ID) {
      return buildHomeSections(leagueMatches, favorites, selectedDate, language);
    }

    return buildLeagueSections(matches, realLeagueId, language);
  }, [leagueMatches, favorites, selectedDate, matches, language, realLeagueId]);

  return {
    sections,
    leagueMatches,
    matches,
  };
};

export type { MatchesSection };

